import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/how-green-are-electric-cars.webp";
import HeroImgMobile       from "../../assets/images/blogs/how-green-are-electric-cars-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   HowGreenAreElectricCars.jsx
   Prefix      : gc-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title  : CENTERED (1 line)
   - No subheads, no images in body, no bullets
   - All regular gc-para
   - Newsletter  : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .gc-wrap *, .gc-wrap *::before, .gc-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .gc-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .gc-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .gc-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .gc-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .gc-fade-up.gc-in { opacity: 1; transform: translateY(0); }
  .gc-delay-1 { transition-delay: 0.08s; }
  .gc-delay-2 { transition-delay: 0.18s; }
  .gc-delay-3 { transition-delay: 0.28s; }
  .gc-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .gc-hero { width: 100%; background: #1A1A1A; }
  .gc-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .gc-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .gc-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .gc-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .gc-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  .gc-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }
    .gc-para:last-child {  margin-bottom: 0px;}


  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .gc-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .gc-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    
    object-position: center;
    z-index: 0;
  }
  .gc-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .gc-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .gc-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .gc-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .gc-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .gc-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .gc-newsletter__input::placeholder { color: #FFFFFF; }
  .gc-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .gc-newsletter__btn:hover { background: #5e2e90; }
  .gc-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .gc-newsletter__btn:hover .gc-newsletter__btn-arrow { transform: translateX(4px); }
  .gc-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .gc-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .gc-inner { padding-left: 32px; padding-right: 32px; }
    .gc-hero__img-wrap img { max-height: 350px; }
    .gc-hero__title-bar { padding: 24px 0px 20px; }
    .gc-hero__title { font-size: 24px; }
    .gc-body-section { padding: 40px 0 48px; }
    .gc-para { margin-bottom: 18px; font-size: 15px; }

    .gc-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .gc-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .gc-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .gc-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .gc-newsletter__heading { font-size: 24px; }
    .gc-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .gc-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .gc-newsletter__input { border-radius: 5px; height: 44px; }
    .gc-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .gc-inner { padding-left: 16px; padding-right: 16px; }
    .gc-hero__title-bar { padding: 30px 0px; }
    .gc-body-section { padding: 30px 0 30px; }
    .gc-hero__img-wrap img { max-height: 366px; width: 100%; }
    .gc-hero__title { font-size: 26px; line-height: 120%; }
    .gc-para { font-size: 14px; margin-bottom: 14px; }

    .gc-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .gc-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .gc-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .gc-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .gc-newsletter__heading { font-size: 30px; line-height: 120%; }
    .gc-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .gc-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .gc-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .gc-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".gc-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("gc-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="gc-newsletter">
      {!isMobile && (
        <>
          <img className="gc-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="gc-newsletter__overlay" />
        </>
      )}
      <div className="gc-newsletter__inner">
        {isMobile && (
          <img className="gc-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="gc-newsletter__content">
          <h2 className="gc-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="gc-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="gc-newsletter__form">
            <input
              className="gc-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="gc-newsletter__btn">
              Subscribe
              <span className="gc-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function HowGreenAreElectricCars() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="gc-wrap">

        {/* HERO — centered title */}
        <section className="gc-hero">
          <div className="gc-hero__title-bar">
            <div className="gc-inner">
              <h1 className="gc-hero__title">How 'green' are electric cars?</h1>
            </div>
          </div>
          <div className="gc-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="How green are electric cars"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="gc-body-section">
          <div className="gc-inner">
            <div className="gc-content-col">

              <p className="gc-para gc-fade-up gc-delay-1">With the ban of new petrol and diesel cars in place in the UK by 2030, sales of electric cars are expected to surge. Plug-in hybrid and electric vehicles accounted for more than 1 in 10 vehicle registrations in 2020, up from 1 in 30 in 2019, according to data published by the Society of Motor Manufacturers and Traders.</p>

              <p className="gc-para gc-fade-up gc-delay-1">The International Energy Agency predicts that by 2030, 125 million electric vehicles will be owned around the world and the UK government aims for almost every car and van on the road to be zero emission by 2050.</p>

              <p className="gc-para gc-fade-up gc-delay-1">But just how green are they?</p>

              <p className="gc-para gc-fade-up gc-delay-1">Electric cars are undoubtedly cleaner than fossil fuel run cars. Although more energy is required to make electric vehicles than petrol, you still save more energy in the long run. The deficit is paid off quickly and even, when with no alternative, the electricity used to charge the vehicles is driven by fossil fuels, they are still greener.</p>

              <p className="gc-para gc-fade-up gc-delay-1">However, it is important to address the huge implications for our natural resources not only to produce green technologies like electric cars, but to keep them charged.</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}